import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Frame,
  Toast,
  Spinner,
  Box,
  BlockStack,
  Banner,
  Button,
  InlineStack,
  TextField,
} from '@shopify/polaris';
import { api } from '../api';

function logoPreviewSrc(settings, cacheBust = 0) {
  if (!settings) return '';
  let base = '';
  if (settings.logo_preview_url) {
    base = settings.logo_preview_url;
  } else if (settings.logo_path) {
    base = `/${String(settings.logo_path).replace(/^\/+/, '')}`;
  } else {
    base = settings.logo_url || '';
  }
  if (!base) return '';
  const sep = base.includes('?') ? '&' : '?';
  return cacheBust ? `${base}${sep}v=${cacheBust}` : base;
}

export default function ConfigurationPage() {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logoAlt, setLogoAlt] = useState('');
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [previewKey, setPreviewKey] = useState(0);
  const fileInputRef = useRef(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/settings');
      const data = res.data?.data || null;
      setSettings(data);
      setLogoAlt(data?.logo_alt_text || '');
    } catch (err) {
      setToastMessage(err?.response?.data?.error || err.message || 'Failed to load settings');
      setToastActive(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      if (logoAlt.trim()) {
        formData.append('logo_alt_text', logoAlt.trim());
      }
      await api.post('/api/settings/logo', formData);
      setToastMessage('Logo uploaded');
      setToastActive(true);
      setPreviewKey(Date.now());
      await fetchSettings();
    } catch (err) {
      setToastMessage(err?.response?.data?.error || err.message || 'Upload failed');
      setToastActive(true);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Remove the store logo?')) return;
    setUploading(true);
    try {
      await api.delete('/api/settings/logo');
      setToastMessage('Logo removed');
      setToastActive(true);
      await fetchSettings();
    } catch (err) {
      setToastMessage(err?.response?.data?.error || err.message || 'Remove failed');
      setToastActive(true);
    } finally {
      setUploading(false);
    }
  };

  const toastMarkup = toastActive ? (
    <Toast content={toastMessage} onDismiss={() => setToastActive(false)} />
  ) : null;

  return (
    <Frame>
      {toastMarkup}
      <Page title="Configuration">
        <Layout>
          <Layout.Section>
            <Banner tone="info">
              <p>
                Upload your store logo here. It is used in email templates via the{' '}
                <strong>{'{{store_logo_block}}'}</strong> placeholder (and{' '}
                <strong>{'{{store_logo_url}}'}</strong> for custom HTML). Add{' '}
                <strong>{'{{store_logo_block}}'}</strong> in the email header if it is not
                already there, or use <strong>Reset to default</strong> on the Contact Us /
                Salesperson email tabs.
              </p>
            </Banner>
          </Layout.Section>

          <Layout.Section>
            <Card>
              {loading ? (
                <InlineStack align="center">
                  <Spinner size="large" />
                </InlineStack>
              ) : (
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Store logo
                  </Text>
                  <Text as="p" tone="subdued">
                    PNG, JPG, GIF, or WebP. Max 2 MB. Recommended width about 160–320px for
                    emails.
                  </Text>

                  {settings?.has_logo ? (
                    <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                      <img
                        src={logoPreviewSrc(settings, previewKey)}
                        alt={settings.logo_alt_text || 'Store logo'}
                        style={{ maxWidth: '200px', maxHeight: '80px', display: 'block' }}
                      />
                    </Box>
                  ) : (
                    <Text as="p" tone="subdued">
                      No logo uploaded yet.
                    </Text>
                  )}

                  <TextField
                    label="Logo alt text (accessibility)"
                    value={logoAlt}
                    onChange={setLogoAlt}
                    autoComplete="off"
                    helpText="Used as the logo alt text in emails. Defaults to your store name if left empty on upload."
                  />

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />

                  <InlineStack gap="300">
                    <Button variant="primary" loading={uploading} onClick={handleUploadClick}>
                      {settings?.has_logo ? 'Replace logo' : 'Upload logo'}
                    </Button>
                    {settings?.has_logo && (
                      <Button tone="critical" loading={uploading} onClick={handleRemoveLogo}>
                        Remove logo
                      </Button>
                    )}
                  </InlineStack>
                </BlockStack>
              )}
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    </Frame>
  );
}
